import { useEffect, useRef, type RefObject } from 'react';

interface UseViewabilityTrackerProps {
  onViewable: () => void;
  threshold?: number;
  once?: boolean;
}

export const useViewabilityTracker = <T extends HTMLElement>({
  onViewable,
  threshold = 0.5,
  once = true,
}: UseViewabilityTrackerProps): RefObject<T | null> => {
  const elementRef = useRef<T>(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onViewable();
          if (once) {
            observer.disconnect();
          }
        }
      },
      { threshold }
    );

    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [onViewable, threshold, once]);

  return elementRef;
};

export default useViewabilityTracker;
